import { useCallback, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import * as atoms from '@elementary/common-ui/atoms';
import { getAgentTestChangeRowId } from '@elementary/common-ui/types';
import { useSetRecoilState } from 'recoil';
import AddTestWizardPage from 'shared/components/AddTestWizard/AddTestWizardPage';
import { PlanWizardState } from 'shared/components/AddTestWizard/types';
import useUrlWithEnv from 'shared/hooks/useUrlWithEnv';
import { AddTestForm } from 'shared/types';
import { applyFormToCloudChange } from 'testManagement/utils/cloudPlanWizardMapping';
import { applyFormToCodeChange } from 'testManagement/utils/codePlanWizardMapping';
import { applyFormToCustomSqlChange } from 'testManagement/utils/customSqlPlanWizardMapping';
import { isWizardResourceType } from 'testManagement/utils/utils';

const isPlanWizardState = (state: unknown): state is PlanWizardState => {
  if (!state || typeof state !== 'object') return false;
  const candidate = state as PlanWizardState;
  const source = candidate.planSource;
  return (
    (source === 'cloud-plan' ||
      source === 'code-plan' ||
      source === 'custom-sql-plan') &&
    !!candidate.planRowId &&
    isWizardResourceType(candidate.wizardResourceType) &&
    !!candidate.initialValues
  );
};

const PlanEditTestWizardRoute = () => {
  const { state } = useLocation();
  const getUrlWithEnv = useUrlWithEnv();
  const setCloudPlan = useSetRecoilState(atoms.agentCloudTestConfigPlan);
  const setCodePlan = useSetRecoilState(atoms.agentCodeTestConfigPlan);
  const setCustomSqlPlan = useSetRecoilState(
    atoms.agentCustomSqlTestConfigPlan,
  );

  const planStateRef = useRef<PlanWizardState | null>(null);
  if (isPlanWizardState(state)) {
    planStateRef.current = state;
  } else if (state != null) {
    planStateRef.current = null;
  }

  const planState = planStateRef.current;

  const handlePlanApply = useCallback(
    (data: AddTestForm, dirtyFields: Record<string, unknown>) => {
      if (!planState) return;
      const { planRowId } = planState;

      if (planState.planSource === 'custom-sql-plan') {
        setCustomSqlPlan((prev) =>
          !prev
            ? prev
            : {
                ...prev,
                changes: prev.changes.map((change) =>
                  getAgentTestChangeRowId(change) !== planRowId
                    ? change
                    : applyFormToCustomSqlChange(change, data, dirtyFields),
                ),
              },
        );
        return;
      }

      if (planState.planSource === 'code-plan') {
        setCodePlan((prev) =>
          !prev
            ? prev
            : {
                ...prev,
                changes: prev.changes.map((change) =>
                  getAgentTestChangeRowId(change) !== planRowId
                    ? change
                    : applyFormToCodeChange(change, data, dirtyFields),
                ),
              },
        );
        return;
      }

      setCloudPlan((prev) =>
        !prev
          ? prev
          : {
              ...prev,
              changes: prev.changes.map((change) =>
                getAgentTestChangeRowId(change) !== planRowId
                  ? change
                  : applyFormToCloudChange(change, data, dirtyFields),
              ),
            },
      );
    },
    [planState, setCloudPlan, setCodePlan, setCustomSqlPlan],
  );

  if (!planState) {
    return <Navigate to={getUrlWithEnv('/test-management')} replace />;
  }

  return (
    <AddTestWizardPage
      wizardMode="plan"
      planSource={planState.planSource}
      planRowId={planState.planRowId}
      planEnvId={planState.planEnvId}
      wizardResourceType={planState.wizardResourceType}
      initialValues={planState.initialValues}
      onPlanApply={handlePlanApply}
    />
  );
};

export default PlanEditTestWizardRoute;
